import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Container, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { FileDownload, InfoOutlined, Print, Receipt } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import { useNavigation } from "../contexts/RouterContext";
import { filterFieldSx } from "../theme/filterStyles";
import { colors } from "../theme/designSystem";
import { paidNotAttendedService, PaidNotAttendedReport, PaidNotAttendedRow } from "../services/paidNotAttendedService";

/**
 * Pagos não realizados (RN-215, item 146, pedido da Lumiar): sessões que o paciente PAGOU e que
 * não aconteceram. A tela existe para a clínica decidir o que fazer com cada linha, e por isso
 * mostra confirmação e presença lado a lado — é o par que separa a falta cobrável (fica com a
 * clínica) do cancelamento (gera crédito) e da sessão que só ficou sem registro de presença.
 */

const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const primeiroDiaDoMes = () => { const h = new Date(); return localIso(new Date(h.getFullYear(), h.getMonth(), 1)); };
const moeda = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dataHora = (iso: string) => { const d = new Date(iso); return { data: d.toLocaleDateString("pt-BR"), hora: d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) }; };

const ReportPaidNotAttended: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();

  const [from, setFrom] = useState(primeiroDiaDoMes);
  const [to, setTo] = useState(() => localIso(new Date()));
  const [professionalId, setProfessionalId] = useState("");
  const [professionalTypeId, setProfessionalTypeId] = useState("");
  const [presence, setPresence] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [data, setData] = useState<PaidNotAttendedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(ITENS_POR_PAGINA_PADRAO);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setData(await paidNotAttendedService.get({ from, to, professionalId, professionalTypeId, presence, confirmation }));
      setPagina(1);
    } catch {
      setErro("Não foi possível carregar o relatório.");
    } finally {
      setLoading(false);
    }
  }, [from, to, professionalId, professionalTypeId, presence, confirmation]);

  useEffect(() => { carregar(); }, [carregar]);

  const linhas = useMemo(() => data?.items ?? [], [data]);
  const daPagina = useMemo(
    () => linhas.slice((pagina - 1) * porPagina, pagina * porPagina),
    [linhas, pagina, porPagina],
  );

  const temFiltro = professionalId !== "" || professionalTypeId !== "" || presence !== "" || confirmation !== ""
    || from !== primeiroDiaDoMes() || to !== localIso(new Date());

  const limpar = () => {
    setFrom(primeiroDiaDoMes());
    setTo(localIso(new Date()));
    setProfessionalId("");
    setProfessionalTypeId("");
    setPresence("");
    setConfirmation("");
  };

  const exportar = async () => {
    try {
      const blob = await paidNotAttendedService.exportXlsx({ from, to, professionalId, professionalTypeId, presence, confirmation });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pagos_nao_realizados_${from}_a_${to}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setErro("Não foi possível gerar a planilha.");
    }
  };

  // O recibo abre na mesma rota que o caixa do paciente usa para reimprimir.
  const abrirRecibo = (paymentId: string) =>
    window.open(`${window.location.pathname}?page=payment-receipt&id=${paymentId}`, "_blank", "noopener");

  return (
    <Box className="c4u-print-page" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Pagos não realizados</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Sessões que o paciente pagou e que não aconteceram, no período escolhido.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField size="small" label="De" type="date" value={from} onChange={(e) => setFrom(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="Até" type="date" value={to} onChange={(e) => setTo(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                <TextField select size="small" label="Especialidade" value={professionalTypeId} onChange={(e) => setProfessionalTypeId(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todas</MenuItem>
                  {(data?.specialties ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Profissional" value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todos</MenuItem>
                  {(data?.professionals ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Presença" value={presence} onChange={(e) => setPresence(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todas</MenuItem>
                  {(data?.presences ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Confirmação" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todas</MenuItem>
                  {(data?.confirmations ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={limpar} disabled={!temFiltro || loading} />
                <Tooltip title="Imprimir" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => window.print()} disabled={loading || linhas.length === 0} aria-label="Imprimir">
                      <Print />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar para Excel" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={exportar} disabled={loading || linhas.length === 0} aria-label="Exportar">
                      <FileDownload />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <Box className="c4u-info-box no-print">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Typography>
                Entram as sessões já pagas cuja presença não é "Compareceu": faltas, cancelamentos e as que
                ficaram sem registro. Sessão futura já paga não entra, porque é pré-pagamento e não pendência.
                Leia a confirmação junto com a presença: cancelamento costuma virar crédito, falta cobrável fica
                com a clínica, e "sem registro de presença" quase sempre se resolve marcando a presença na agenda.
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : erro ? (
              <Typography sx={{ textAlign: "center", color: "#dc3545", py: 4 }}>{erro}</Typography>
            ) : (
              <>
                <Box sx={summaryCardsRowSx} className="c4u-print-block">
                  <ReportSummaryCard label="Sessões pagas e não realizadas" value={String(data?.count ?? 0)} />
                  <ReportSummaryCard label="Valor total" value={moeda(data?.total ?? 0)} />
                  <ReportSummaryCard label="Só falta marcar a presença" value={String(data?.withoutPresenceCount ?? 0)} sub={moeda(data?.withoutPresenceTotal ?? 0)} subColor="#b8860b" />
                </Box>

                <ResultCount shown={linhas.length} />

                <Box className="c4u-table">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 92px" }}>Data</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 96px" }}>Horário</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "1 1 190px" }}>Paciente</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 170px" }}>Atendimento</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "1 1 150px" }}>Profissional</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 1 150px" }}>Confirmação</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 1 160px" }}>Presença</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 96px" }}>Pago em</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 104px", textAlign: "right" }}>Valor</Box>
                    <Box className="admin-plans-header-cell admin-plans-actions no-print" sx={{ flex: "0 0 64px" }}>Recibo</Box>
                  </Box>

                  {daPagina.length === 0 ? (
                    <Typography sx={{ px: 2, py: 3, fontSize: 13, color: "#adb5bd", textAlign: "center" }}>
                      Nenhuma sessão paga e não realizada no período. É o resultado que se quer.
                    </Typography>
                  ) : (
                    daPagina.map((r: PaidNotAttendedRow) => {
                      const inicio = dataHora(r.start);
                      const fim = dataHora(r.end);
                      const semPresenca = r.presence === "Sem registro de presença";
                      return (
                        <Box key={r.scheduleId} className="c4u-table__row">
                          <Box className="admin-plans-cell" data-label="Data" sx={{ flex: "0 0 92px" }}>{inicio.data}</Box>
                          <Box className="admin-plans-cell" data-label="Horário" sx={{ flex: "0 0 96px" }}>{inicio.hora}–{fim.hora}</Box>
                          <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: "1 1 190px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{r.patientName}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--lg" data-label="Atendimento" sx={{ flex: "1 1 170px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{r.serviceName}</Typography>
                            {r.specialtyName && <Typography sx={{ fontSize: 11, color: "#6c757d" }}>{r.specialtyName}</Typography>}
                          </Box>
                          <Box className="admin-plans-cell c4u-col--xl" data-label="Profissional" sx={{ flex: "1 1 150px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{r.professionalName}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--lg" data-label="Confirmação" sx={{ flex: "0 1 150px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12 }}>{r.confirmation}</Typography>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Presença" sx={{ flex: "0 1 160px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, color: semPresenca ? "#b8860b" : "#212529", fontWeight: semPresenca ? 600 : 400 }}>
                              {r.presence}
                            </Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--xl" data-label="Pago em" sx={{ flex: "0 0 96px" }}>
                            {r.paymentDate ? new Date(r.paymentDate).toLocaleDateString("pt-BR") : "—"}
                          </Box>
                          <Box className="admin-plans-cell" data-label="Valor" sx={{ flex: "0 0 104px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {moeda(r.value)}
                          </Box>
                          <Box className="admin-plans-cell admin-plans-actions no-print" sx={{ flex: "0 0 64px" }}>
                            {r.paymentId ? (
                              <Tooltip title="Abrir recibo" arrow>
                                <IconButton className="c4u-btn-icon" onClick={() => abrirRecibo(r.paymentId!)} sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: "#029AAB" } }} aria-label="Abrir recibo">
                                  <Receipt sx={{ fontSize: "1rem", color: "#fff" }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Baixa feita na agenda, sem recibo no caixa" arrow>
                                <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>—</Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Box className="no-print">
                  <StandardPagination
                    currentPage={pagina}
                    totalPages={Math.max(1, Math.ceil(linhas.length / porPagina))}
                    itemsPerPage={porPagina}
                    totalItems={linhas.length}
                    onPageChange={setPagina}
                    onItemsPerPageChange={(n) => { setPorPagina(n); setPagina(1); }}
                  />
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportPaidNotAttended;
